import React from "react";
import { useParams } from "react-router-dom";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import CtaNomal from "../../components/CtaNomal";

import "../../styles/common.css";
import "../../styles/work.css";

import { works, resolveWork } from "../../data/works";
import { tagLabels } from "../../data/tagLabels";
import { relatedScore } from "../../utils/relatedWorks";

const lang = "ja";

const buildDisplayTags = (w) => {
  const industry = (w.industryTags ?? []).slice(0, 2);
  const companyCountry = w.company?.country ? [`companyCountry:${w.company.country}`] : [];
  return [...industry, ...companyCountry];
};

export default function WorkDetailTemplate() {
  const { slug } = useParams();

  const resolvedWorks = works.map(resolveWork);
  const work = resolvedWorks.find((w) => w.slug === slug);

  if (!work) return null;

  const relatedWorks = resolvedWorks
    .filter((w) => w.slug !== slug)
    .map((w) => ({ w, score: relatedScore(w, work), sameCategory: w.category === work.category }))
    .sort((a, b) => {
      if (a.sameCategory !== b.sameCategory) return a.sameCategory ? -1 : 1;
      if (b.score !== a.score) return b.score - a.score;
      return (a.w.title?.[lang] ?? "").localeCompare(b.w.title?.[lang] ?? "");
    })
    .filter(({ score }) => score > 0)
    .slice(0, 12);

  const hasList = (arr) => Array.isArray(arr) && arr.length > 0;
  const hasText = (v) => typeof v === "string" ? v.trim().length > 0 : !!v;

  const clientName = work.client?.name?.[lang] ?? "";
  const industryList = (work.industryTags ?? []).map(
    (tag) => tagLabels[tag]?.[lang] ?? tag
  );
  const companyLocation =
    tagLabels[`companyCountry:${work.company?.country}`]?.[lang] ?? "";
  const overview = work.overview?.[lang] ?? "";
  const projectDetails = (work.serviceTags ?? []).map(
    (tag) => tagLabels[tag]?.[lang] ?? tag
  );

  const serviceAreaList = [
    ...(work.target?.regions ?? []).map(
      (r) => tagLabels[`targetRegion:${r}`]?.[lang] ?? r
    ),
    ...(work.target?.countries ?? []).map(
      (c) => tagLabels[`targetCountry:${c}`]?.[lang] ?? c
    ),
  ];

  const objectiveList = (work.purposeTags ?? []).map(
    (tag) => tagLabels[tag]?.[lang] ?? tag
  );

  const background = work.requestBackground?.[lang] ?? "";
  const release = work.release?.[lang] ?? "";
  const website = work.website ?? "";

  const rows = [
    { label: "クライアント", value: clientName, type: "text" },
    { label: "業種", value: industryList, type: "list", className: "industry" },
    { label: "企業所在地", value: companyLocation, type: "text" },
    { label: "会社概要", value: overview, type: "text" },
    { label: "仕事内容", value: projectDetails, type: "list", className: "jobPart" },
    { label: "対象エリア", value: serviceAreaList, type: "list" },
    { label: "目的", value: objectiveList, type: "list", className: "purpose" },
    { label: "依頼背景", value: background, type: "text" },
    { label: "リリース", value: release, type: "text" },
    { label: "企業サイト", value: website, type: "link" },
  ].filter((row) => {
    if (row.type === "list") return hasList(row.value);
    return hasText(row.value);
  });

  const hasPc = !!work.webImages?.pc;
  const hasSp = !!work.webImages?.sp;
  const isOnlySp = hasSp && !hasPc;


  return (
    <>
      <Navbar />
      <main>
        <section className="fvUnder">
          <div className="fvUnder-text">
            <h1>{work.title?.[lang]}</h1>
            <nav aria-label="Breadcrumb" className="breadcrumb">
              <ol className="breadcrumb">
                <li><a href="/">Home</a></li>
                <li><a href="/work">制作実績</a></li>
                <li aria-current="page">{work.title?.[lang]}</li>
              </ol>
            </nav>
          </div>
        </section>

        <section className="workDetail">
          <div
            className={`workDetail-main ${work.mainVideo?.isPortrait ? "workDetail-main--portrait" : ""
              }`}
          >
            {work.mainVideo?.src ? (
              <video controls playsInline poster={work.mainVideo.poster}>
                <source src={work.mainVideo.src} type="video/mp4" />
                お使いのブラウザは動画の再生に対応していません。
              </video>
            ) : (
              <img src={work.thumbnail} alt={work.title?.[lang] ?? ""} />
            )}
          </div>

          <div className="workDetail-text">
            {Array.isArray(work.ctas) && work.ctas.length > 0 && (
              <div className="workDetail-cta">
                {work.ctas.map((cta, index) => (
                  <div
                    key={`${cta.url}-${index}`}
                    className="button-content button-arrow button-black"
                  >
                    <a href={cta.url} target="_blank" rel="noopener noreferrer">
                      {cta.label?.[lang] ?? cta.label}
                    </a>
                  </div>
                ))}
              </div>
            )}
            <table className="workDetail-text__table">
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label}>
                    <th>{row.label}</th>
                    <td>
                      {row.type === "list" ? (
                        <ul className={row.className}>
                          {row.value.map((v) => (
                            <li key={v}>{v}</li>
                          ))}
                        </ul>
                      ) : row.type === "link" ? (
                        <a href={row.value} target="_blank" rel="noopener noreferrer" class="button-underLine">
                          {row.value}
                        </a>
                      ) : (
                        row.value
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

          </div>
          {/* {Array.isArray(work.images) && work.images.length > 0 && (
            <>
              {work.images.map((src, i) => (
                <div className="workDetail-mock" key={`${work.slug}-img-${i}`}>
                  <img src={src} alt={`${work.title?.[lang] ?? ""} ${i + 1}`} />
                </div>
              ))}
            </>
          )} */}
          {Array.isArray(work.images) && work.images.length > 0 ? (
            <>
              {work.images.map((src, i) => (
                <div className="workDetail-mock" key={`${work.slug}-img-${i}`}>
                  <img src={src} alt={`${work.title?.[lang] ?? ""} ${i + 1}`} />
                </div>
              ))}
            </>
          ) : Array.isArray(work.videos) && work.videos.length > 0 ? (
            <>
              {work.videos.map((video, i) => (
                <div
                  className={`workDetail-mock ${video.isPortrait ? "portrait" : ""}`}
                  key={`${work.slug}-video-${i}`}
                >
                  <video controls playsInline poster={work.mainVideo.poster}>
                    <source src={video.src} type="video/mp4" />
                    お使いのブラウザは動画の再生に対応していません。
                  </video>
                </div>
              ))}
            </>
          ) : null}


          {work.webImages && (hasPc || hasSp) && (
            <div className={`workDetail-webImg ${isOnlySp ? "only-sp" : ""}`}>
              {hasPc && (
                <div className="workDetail-webImg__pc">
                  <img
                    src={work.webImages.pc}
                    alt={`${work.title?.[lang] ?? ""} PC`}
                  />
                </div>
              )}
              {hasSp && (
                <div className="workDetail-webImg__sp">
                  <img
                    src={work.webImages.sp}
                    alt={`${work.title?.[lang] ?? ""} SP`}
                  />
                </div>
              )}
            </div>
          )}
        </section>

        <section className="otherWork">
          <div className="title">
            <p className="title-lead">OTHER WORKS</p>
            <h2>関連する制作実績</h2>
          </div>

          <div className="work-box">
            {relatedWorks.map(({ w }) => {
              const displayTags = buildDisplayTags(w);
              return (
                <article key={w.slug}>
                  <a href={`/work/${w.slug}`}>
                    <div className="work-box__image">
                      <img src={w.thumbnail} alt={w.title?.[lang] ?? ""} />
                    </div>
                    <div className="work-box__text">
                      <h3>{w.title?.[lang]}</h3>
                      {displayTags.length > 0 && (
                        <ul className="work-box__tag">
                          {displayTags.map((tag) => (
                            <li key={tag}>{tagLabels[tag]?.[lang] ?? tag}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </a>
                </article>
              );
            })}
          </div>

          <div className="button-content button-arrow button-black">
            <a href="/work">すべての制作実績を見る</a>
          </div>
        </section>

        <CtaNomal />
      </main>
      <Footer />
    </>
  );
}
